import type { QuestionLong as QuestionLongT } from '@repo/lib/quiz/questions/class/QuestionLong';
import type { ChangeEvent } from 'react';

import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useEffect, useMemo, useState } from 'react';

import type { QuestionLongProps } from '@/components/features/quiz/question/question-resolver/question-long';

import { useQuestionState } from '@/hooks/questions';

export function useQuestionLong(useQuestionLongTextareaProps: QuestionLongProps) {
  const { question, sectionIndex, questionIndex } = useQuestionLongTextareaProps;

  const { getMarked, setAnswer, getAnswer, exportedAnswer } = useQuestionState<QuestionLongT>(question, sectionIndex, questionIndex);
  const selectedAnswer = getAnswer();
  const marked = getMarked();
  const exportedUserAnswer = exportedAnswer();

  const [value, setValue] = useState<string | undefined>(selectedAnswer?.answer ?? undefined);

  const debouncedValue = useDebounceValue(value, 650);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const status = useMemo(() => {
    const userAnswer: string = value ?? '';
    const len: number = userAnswer.length;
    const minLength: number = question.minLength ?? 0;
    const maxLength: number = question.maxLength ?? Number.MAX_SAFE_INTEGER;

    if (minLength === 0 && maxLength === Number.MAX_SAFE_INTEGER) {
      return { message: '', className: 'hidden' };
    }

    if (len < minLength) {
      const needed: number = minLength - len;
      return {
        message: `Potrzebujesz jeszcze ${needed} znaków`,
        className: 'text-text-destructive',
      };
    } else if (len > maxLength) {
      const excess = len - maxLength;
      return {
        message: `Za dużo o ${excess} znaków`,
        className: 'text-text-destructive',
      };
    } else if (maxLength === Number.MAX_SAFE_INTEGER) {
      return {
        message: `${len} znaków`,
        className: 'text-text-tertiary',
      };
    } else {
      return {
        message: `${len}/${maxLength} znaków`,
        className: 'text-text-tertiary',
      };
    }
  }, [selectedAnswer, question.minLength, question.maxLength]);

  useEffect(() => {
    if (debouncedValue !== undefined) {
      setAnswer(debouncedValue);
    }
  }, [debouncedValue, setAnswer]);

  return {
    handleInputChange,
    status,
    value: value,
    marked,
    exportedUserAnswer,
  };
}
